import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Eye, EyeOff, ArrowLeft, User, Building2, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';

const roles = [
  { value: 'buyer', label: 'Buyer / Investor', icon: User, desc: 'Looking to buy or invest in properties' },
  { value: 'seller', label: 'Seller / Landlord', icon: Building2, desc: 'Looking to sell or rent your property' },
  { value: 'agent', label: 'Agent', icon: Briefcase, desc: 'Real estate professional' },
];

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    full_name: '',
    phone: '',
    role: 'buyer'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await register(formData);
    
    if (result.success) {
      navigate('/onboarding');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="noise-overlay" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" />
      
      <div className="w-full max-w-lg relative z-10">
        <Button 
          variant="ghost" 
          className="mb-8 hover:text-primary"
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {step > 1 ? 'Back' : 'Back to Home'}
        </Button>
        
        <Card className="glass-card border-white/10">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded logo-icon-metallic flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-primary-foreground" />
            </div>
            <CardTitle className="font-display text-2xl">Create Your Account</CardTitle>
            <CardDescription>
              {step === 1 ? 'Choose your role on Rashii' : 'Enter your details'}
            </CardDescription>
            
            {/* Progress */}
            <div className="flex gap-2 justify-center mt-4">
              <div className={`w-8 h-1 rounded ${step >= 1 ? 'bg-primary' : 'bg-secondary'}`} />
              <div className={`w-8 h-1 rounded ${step >= 2 ? 'bg-primary' : 'bg-secondary'}`} />
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            {error && (
              <div className="p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive text-sm mb-6" data-testid="register-error">
                {error}
              </div>
            )}
            
            {step === 1 ? (
              <div className="space-y-4">
                {roles.map((role) => (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, role: role.value });
                      setStep(2);
                    }}
                    className={`w-full p-4 rounded border text-left transition-all duration-300 flex items-start gap-4 hover:border-primary/50 ${
                      formData.role === role.value ? 'border-primary bg-primary/10' : 'border-white/10 bg-transparent'
                    }`}
                    data-testid={`role-${role.value}-btn`}
                  >
                    <div className="w-10 h-10 rounded bg-secondary flex items-center justify-center flex-shrink-0">
                      <role.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{role.label}</div>
                      <div className="text-sm text-muted-foreground">{role.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Full Name
                    </Label>
                    <Input
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="bg-transparent border-white/20 focus:border-primary h-12"
                      data-testid="register-fullname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Username
                    </Label>
                    <Input
                      placeholder="johndoe"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="bg-transparent border-white/20 focus:border-primary h-12"
                      required
                      data-testid="register-username-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-transparent border-white/20 focus:border-primary h-12"
                    required
                    data-testid="register-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Phone Number
                  </Label>
                  <Input
                    placeholder="+971 50 123 4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-transparent border-white/20 focus:border-primary h-12"
                    data-testid="register-phone-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-transparent border-white/20 focus:border-primary h-12 pr-10"
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold h-12"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            )}
            
            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign In
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
